/**
 * @file Contains helper functions for RemindersPicker component
 */

import { PREFERENCE_REMINDERS_LIST } from '../../../constants/preferences';
import { Reminder, TimeUnit } from '../../../generated/graphql';
import translate from '../../../i18n/translate';
import { ReminderItem } from '../../../models/reminder';
import { getLocaleCode } from '../../../utils/user/locale';

/**
 * Generate translation for reminder item
 *
 * @param amount Reminder amount
 * @param unit   Reminder unit
 * @returns      Translation based on amount and unit
 */
export const getTranslation = (amount: number, unit: TimeUnit): string => {
  if (amount === 0) {
    return translate('MODAL__REMINDERS__AT_TIME_OF_DUE_DATE');
  }

  const locale = getLocaleCode();
  const rtf = new Intl.RelativeTimeFormat(locale).formatToParts(
    Math.abs(amount),
    unit.toLowerCase() as unknown as Intl.RelativeTimeFormatUnit,
  );

  return `${rtf[1].value}${rtf[2].value} ${translate(
    amount > 0
      ? 'MODAL__REMINDERS__BEFORE_SUFFIX'
      : 'MODAL__REMINDERS__AFTER_SUFFIX',
  )}`;
};

/**
 * Get reminder items used for Select options
 *
 * @param reminders List of selected reminders (If not provided, default reminders will be used)
 * @returns         List of reminder items
 */
export const getReminderItems = (reminders?: Reminder[]): ReminderItem[] => {
  if (reminders) {
    return reminders.map(reminder => ({
      label: getTranslation(reminder.amount, reminder.unit),
      reminder,
    }));
  }

  return PREFERENCE_REMINDERS_LIST.map(reminder => ({
    label: getTranslation(reminder.amount, reminder.unit),
    reminder,
  }));
};
